import {
	FormikValues,
	PageTypeEnum,
	TableSettingColumnSubTypeMap,
	TableTypes,
	TableValuesMap,
	UpdateFieldObjectNameEnum,
	UserSettingScreenCodeEnum,
	WindowPostMessage,
} from '@/constant';
import { useUpdateFields } from '@/hooks';
import {
	CommonNoticeReceiveActions,
	DefineDocumentAreaActions,
	DefineDocumentAreaSelectors,
	DistrictAllowIpActions,
	FooterInfoActions,
	ForeignApplicantActions,
	ForeignSystemUnitActions,
	ForeignUserActions,
	SystemUnitActions,
	SystemUserActions,
	TDataUpdateFieldsRequest,
	useAppSelector,
	WorkerApplicantActions,
	WorkerAssignmentActions,
	WorkerAssignmentFranchiseActions,
	WorkerChangeWorkPlaceActions,
	WorkerDocumentActions,
	WorkerExtensionStayActions,
	WorkerFranchiseActions,
	WorkerLodgingActions,
	WorkerProgramActions,
	WorkerRecommenderActions,
	WorkerSalaryActions,
	WorkerUnauthorizedDepartureActions,
	WorkerUserActions,
} from '@/stores';
import {
	convertDropdownRelation,
	dayjs,
	formatDataUpdateFields,
	formatDataUpdateList,
	formatDate,
	insertObjectIf,
	insertObjectIfV2,
	toCamelCase,
	trimObjectValues,
} from '@/utils';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { omit } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

type Params<T extends TableTypes> = {
	tableType?: T;
	needToReloadListAfterClosePopup?: boolean;
};

export const useEditableRowSubmit = <T extends TableTypes>(
	params: Params<T>
) => {
	const { tableType, needToReloadListAfterClosePopup } = params;

	const dispatch = useDispatch();
	const paramsGetDocumentList = useAppSelector(
		DefineDocumentAreaSelectors.selectParams
	);
	const { handleUpdate } = useUpdateFields();

	const onSubmit = useCallback(
		(
			values: FormikValues,
			callback?: ({ error, data }: any) => void,
			removeKeys?: string[]
		) => {
			if (!tableType) return;

			let updateTable = '' as UpdateFieldObjectNameEnum;
			let dataUpdate = values as TDataUpdateFieldsRequest['data'];
			let screenCode = TableSettingColumnSubTypeMap[tableType];

			if (isNotNilOrEmpty(removeKeys)) {
				dataUpdate = omit(
					removeKeys as (keyof typeof dataUpdate)[],
					dataUpdate
				);
			}

			let updateCallbackAction = null as any;
			let needFormatDataUpdateFields = false;

			switch (tableType) {
				case TableTypes.NOTICE_RECEIVE: {
					updateTable = UpdateFieldObjectNameEnum.NOTICE;
					updateCallbackAction =
						CommonNoticeReceiveActions.updateCommonNoticeReceiveListSuccess;
					break;
				}

				case TableTypes.UM1_01: {
					updateTable = UpdateFieldObjectNameEnum.USER;
					screenCode = UserSettingScreenCodeEnum.UM1;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction = SystemUserActions.updateSystemUserSuccess;
					break;
				}
				case TableTypes.MM1_01: {
					screenCode = UserSettingScreenCodeEnum.MM1;
					updateTable = UpdateFieldObjectNameEnum.USER;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction = WorkerUserActions.updateWorkerUserSuccess;
					break;
				}

				case TableTypes.AR1_01: {
					screenCode = UserSettingScreenCodeEnum.AR1;
					updateTable = UpdateFieldObjectNameEnum.APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerApplicantActions.updateWorkerApplicantSuccess;
					break;
				}

				case TableTypes.AR1_03_RECOMMENDED: {
					screenCode = UserSettingScreenCodeEnum.AR1;
					updateTable = UpdateFieldObjectNameEnum.APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerApplicantActions.updateWorkerRecommenderApplicantSuccess;
					break;
				}

				case TableTypes.AR1_03_LODGING: {
					screenCode = UserSettingScreenCodeEnum.AR1;
					updateTable = UpdateFieldObjectNameEnum.APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerApplicantActions.updateWorkerLodgingApplicantSuccess;
					break;
				}

				case TableTypes.RM1_01: {
					screenCode = UserSettingScreenCodeEnum.RM1;
					updateTable = UpdateFieldObjectNameEnum.RECOMMENDER;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerRecommenderActions.updateWorkerRecommenderSuccess;
					break;
				}

				case TableTypes.RM1_03_APPLICANT: {
					screenCode = UserSettingScreenCodeEnum.RM1;
					updateTable = UpdateFieldObjectNameEnum.APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerRecommenderActions.updateWorkerRecommenderApplicantSuccess;
					break;
				}

				case TableTypes.LM1_01: {
					screenCode = UserSettingScreenCodeEnum.LM1;
					updateTable = UpdateFieldObjectNameEnum.LODGING;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerLodgingActions.updateWorkerLodgingSuccess;
					break;
				}

				case TableTypes.LM1_03_APPLICANT: {
					screenCode = UserSettingScreenCodeEnum.LM1;
					updateTable = UpdateFieldObjectNameEnum.APPLICANT;
					updateCallbackAction =
						WorkerLodgingActions.updateWorkerLodgingApplicantSuccess;
					break;
				}

				case TableTypes.PM1_01:
				case TableTypes.PM2_01: {
					screenCode = UserSettingScreenCodeEnum.PM1;
					updateTable = UpdateFieldObjectNameEnum.PROGRAM;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerProgramActions.updateWorkerProgramSuccess;
					break;
				}

				case TableTypes.FR1_01: {
					screenCode = UserSettingScreenCodeEnum.FR1;
					updateTable = UpdateFieldObjectNameEnum.FRANCHISE;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerFranchiseActions.updateWorkerFranchiseSuccess;
					needFormatDataUpdateFields = true;

					break;
				}

				case TableTypes.WC1_01: {
					screenCode = UserSettingScreenCodeEnum.WC1;
					updateTable = UpdateFieldObjectNameEnum.CHANGE_WORK_PLACE;
					if ('changeWorkPlaceReason' in dataUpdate) {
						dataUpdate.changeWorkPlaceDetailReason = '';
					}
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);

					updateCallbackAction =
						WorkerChangeWorkPlaceActions.updateWorkerChangeWorkPlaceSuccess;
					break;
				}

				case TableTypes.DEFINE_DOCUMENT_AREA: {
					// use this function to update type document
					const params = {
						id: values?.id,
						documentType: values?.documentType,
						type: values?.type,
						name: values?.name,
						...insertObjectIfV2(values?.relatedInfoSelect, {
							'reqGroupRefer.refTableName': convertDropdownRelation(
								values?.relatedInfoSelect
							),
						}),
						...insertObjectIfV2(values?.currentRefTableId, {
							currentRefTableId: values?.currentRefTableId,
						}),
						...insertObjectIfV2(values?.currentRefTableName, {
							currentRefTableName: values?.currentRefTableName as PageTypeEnum,
						}),
					};

					const data =
						params as TableValuesMap[TableTypes.DEFINE_DOCUMENT_AREA];

					dispatch(
						DefineDocumentAreaActions.updateDocumentItemRequest({
							data,
							callback: (error) => {
								if (error) {
									//TODO: show error
									dispatch(
										DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
											...paramsGetDocumentList,
										})
									);
									return;
								}
								dispatch(
									DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
										...paramsGetDocumentList,
										callback: () => {
											callback?.({ error, data });
										},
									})
								);
							},
						})
					);
					break;
				}

				case TableTypes.EM1_01: {
					screenCode = UserSettingScreenCodeEnum.EM1;
					updateTable = UpdateFieldObjectNameEnum.EXTENSION_STAY_APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);

					updateCallbackAction =
						WorkerExtensionStayActions.updateWorkerExtensionStaySuccess;
					break;
				}

				case TableTypes.EM1_03_APPLICANT: {
					screenCode = UserSettingScreenCodeEnum.EM1;
					updateTable = UpdateFieldObjectNameEnum.EXTENSION_STAY_APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerExtensionStayActions.updateWorkerExtensionStaySuccess;
					break;
				}

				case TableTypes.BM1_01: {
					screenCode = UserSettingScreenCodeEnum.BM1;
					updateTable = UpdateFieldObjectNameEnum.UNAUTHORIZED_DEPARTURE;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);

					updateCallbackAction =
						WorkerUnauthorizedDepartureActions.updateWorkerUnauthorizedDepartureListSuccess;
					break;
				}

				case TableTypes.PA4_01: {
					screenCode = UserSettingScreenCodeEnum.PA4;
					updateTable = UpdateFieldObjectNameEnum.APPLICANT_SALARY;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerSalaryActions.updateRowWorkerSalarySuccess;
					break;
				}

				case TableTypes.DM1_01: {
					screenCode = UserSettingScreenCodeEnum.DM1;
					updateTable = UpdateFieldObjectNameEnum.FILE;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerDocumentActions.updateWorkerDocumentSuccess;
					break;
				}

				case TableTypes.CM1_01: {
					screenCode = UserSettingScreenCodeEnum.CM1;
					updateTable = UpdateFieldObjectNameEnum.UNIT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction = SystemUnitActions.updateSystemUnitSuccess;
					break;
				}

				case TableTypes.AM1_01: {
					screenCode = UserSettingScreenCodeEnum.AM1;
					updateTable = UpdateFieldObjectNameEnum.ASSIGNMENT_FRANCHISE;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerAssignmentFranchiseActions.updateWorkerAssignmentFranchiseListSuccess;
					break;
				}

				case TableTypes.AM1_03_APPLICANT: {
					screenCode = UserSettingScreenCodeEnum.AM1;
					updateTable = UpdateFieldObjectNameEnum.ASSIGNMENT_APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerAssignmentActions.updateWorkerAssignmentApplicantListSuccess;
					break;
				}

				case TableTypes.AM3_01: {
					screenCode = UserSettingScreenCodeEnum.AM3;
					updateTable = UpdateFieldObjectNameEnum.ASSIGNMENT_APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						WorkerAssignmentActions.updateWorkerAssignmentListSuccess;
					break;
				}

				case TableTypes.OI2_01: {
					screenCode = UserSettingScreenCodeEnum.OI2;
					updateTable = UpdateFieldObjectNameEnum.USER;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction = ForeignUserActions.updateForeignUserSuccess;
					break;
				}

				case TableTypes.OR1_01: {
					screenCode = UserSettingScreenCodeEnum.OR1;
					updateTable = UpdateFieldObjectNameEnum.FOREIGN_APPLICANT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						ForeignApplicantActions.updateForeignApplicantSuccess;
					break;
				}
				case TableTypes.FOOTER_INFO: {
					screenCode = UserSettingScreenCodeEnum.FM1;
					updateTable = UpdateFieldObjectNameEnum.FOOTER_INFO;

					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction = FooterInfoActions.updateFooterInfoListSuccess;

					break;
				}
				case TableTypes.CM2_01: {
					screenCode = UserSettingScreenCodeEnum.CM2;
					updateTable = UpdateFieldObjectNameEnum.UNIT;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						ForeignSystemUnitActions.updateSystemUnitSuccess;
					break;
				}

				case TableTypes.DISTRICT_ALLOW_IP: {
					screenCode = UserSettingScreenCodeEnum.FM1;
					updateTable = UpdateFieldObjectNameEnum.UNIT_DISTRICT_ALLOW_IP;
					dataUpdate = formatDataUpdateList(dataUpdate, updateTable);
					updateCallbackAction =
						DistrictAllowIpActions.updateDistrictAllowIpListSuccess;
					break;
				}

				default: {
					break;
				}
			}

			if (updateTable) {
				handleUpdate({
					objectName: updateTable,
					data: dataUpdate,
					screenCode,
					callback: ({ error, data, fields }) => {
						if (!error) {
							// showToast(TextMessage.TOAST_01);
							const updateKey = `${toCamelCase(updateTable)}UpdateDt`;

							if (needToReloadListAfterClosePopup) {
								window.parent.postMessage({
									message:
										WindowPostMessage.NEED_TO_RELOAD_LIST_AFTER_CLOSE_POPUP,
									values,
								});
							}

							if (updateCallbackAction) {
								dispatch(
									updateCallbackAction?.({
										data: {
											...trimObjectValues(values),
											...insertObjectIf(needFormatDataUpdateFields, {
												...formatDataUpdateFields(updateTable, fields),
											}),
											[updateKey]: formatDate(dayjs()),
										},
									})
								);
							}
						}
						callback?.({ error, data });
					},
				});
			}
		},
		[tableType, handleUpdate]
	);

	return {
		onSubmit,
	};
};
